<template>
  <div>
    <!-- Filtros locales -->
    <div class="mb-3">
      <div class="row g-2 align-items-end">
        <div class="col-md-3">
          <label class="form-label">Número</label>
          <input v-model="filtros.numero" type="text" class="form-control form-control-sm" placeholder="Filtrar..." />
        </div>
        <div class="col-md-2">
          <label class="form-label">Cédula</label>
          <input v-model="filtros.cedula" type="text" class="form-control form-control-sm" placeholder="Filtrar..." />
        </div>
        <div class="col-md-3">
          <label class="form-label">Nombre</label>
          <input v-model="filtros.nombre" type="text" class="form-control form-control-sm" placeholder="Filtrar..." />
        </div>
        <div class="col-md-2">
          <label class="form-label">Archivos</label>
          <select v-model="filtros.conArchivos" class="form-select form-select-sm">
            <option value="">Todos</option>
            <option value="con">Con archivos</option>
            <option value="sin">Sin archivos</option>
          </select>
        </div>
        <div class="col-md-2 d-flex gap-2">
          <button @click="limpiarFiltros" class="btn btn-outline-secondary btn-sm flex-fill">
            Limpiar
          </button>
          <button @click="$emit('refrescar')" class="btn btn-outline-primary btn-sm flex-fill" :disabled="loading">
            Refrescar
          </button>
        </div>
      </div>
    </div>

    <!-- Resumen -->
    <div class="d-flex gap-3 mb-3 flex-wrap" v-if="!loading && datosFiltrados.length">
      <div class="border rounded px-3 py-2 text-center bg-light">
        <div class="fw-bold fs-5">{{ datosFiltrados.length }}</div>
        <div class="text-muted small">Planillas</div>
      </div>
      <div class="border rounded px-3 py-2 text-center bg-light">
        <div class="fw-bold fs-5 text-success">{{ totalArchivos }}</div>
        <div class="text-muted small">Total Archivos</div>
      </div>
      <div class="border rounded px-3 py-2 text-center bg-light">
        <div class="fw-bold fs-5 text-danger">{{ planillasSinArchivos }}</div>
        <div class="text-muted small">Sin Archivos</div>
      </div>
      <div class="border rounded px-3 py-2 text-center bg-light ms-auto">
        <button @click="exportarCSV" class="btn btn-success btn-sm">📥 Exportar CSV</button>
      </div>
    </div>

    <!-- Error -->
    <div v-if="error" class="alert alert-danger">Error: {{ error }}</div>

    <!-- Loading -->
    <div v-if="loading" class="text-center my-4">
      <div class="spinner-border" role="status">
        <span class="visually-hidden">Cargando...</span>
      </div>
    </div>

    <!-- Tabla -->
    <div v-if="!loading" class="table-responsive">
      <table class="table table-bordered table-sm">
        <thead class="table-dark">
          <tr>
            <th @click="ordenarPor('numero')"        class="th-sort">Número {{ iconOrden('numero') }}</th>
            <th @click="ordenarPor('cedula')"        class="th-sort">Cédula {{ iconOrden('cedula') }}</th>
            <th @click="ordenarPor('nombre')"        class="th-sort">Nombre {{ iconOrden('nombre') }}</th>
            <th @click="ordenarPor('totalArchivos')" class="th-sort text-center">Archivos {{ iconOrden('totalArchivos') }}</th>
          </tr>
        </thead>
        <tbody>
          <tr
            v-for="item in datosPaginados"
            :key="item.idPlanilla"
            :class="{ 'table-danger': item.totalArchivos == 0 }"
          >
            <td>{{ item.numero }}</td>
            <td>{{ item.cedula }}</td>
            <td>{{ item.nombre }}</td>
            <td class="text-center">
              <span class="badge" :class="item.totalArchivos > 0 ? 'bg-success' : 'bg-danger'">
                {{ item.totalArchivos }}
              </span>
            </td>
          </tr>
          <tr v-if="datosFiltrados.length === 0">
            <td colspan="4" class="text-center text-muted py-3">No hay resultados.</td>
          </tr>
        </tbody>
      </table>
    </div>

    <!-- Paginación -->
    <div class="d-flex justify-content-center mt-3 gap-2 align-items-center">
      <button class="btn btn-outline-secondary btn-sm" :disabled="paginaActual === 1" @click="paginaActual = 1">«</button>
      <button class="btn btn-outline-secondary btn-sm" :disabled="paginaActual === 1" @click="paginaActual--">‹</button>
      <span class="small">Página {{ paginaActual }} de {{ totalPaginas }} ({{ datosFiltrados.length }} registros)</span>
      <button class="btn btn-outline-secondary btn-sm" :disabled="paginaActual === totalPaginas" @click="paginaActual++">›</button>
      <button class="btn btn-outline-secondary btn-sm" :disabled="paginaActual === totalPaginas" @click="paginaActual = totalPaginas">»</button>
      <select v-model.number="porPagina" class="form-select form-select-sm" style="width:80px">
        <option :value="10">10</option>
        <option :value="25">25</option>
        <option :value="50">50</option>
      </select>
    </div>
  </div>
</template>

<script>
import { ref, computed, watch } from 'vue'

export default {
  name: 'PlanillaArchivoTab',
  props: {
    data:    { type: Array,   default: () => [] },
    loading: { type: Boolean, default: false },
    error:   { type: String,  default: null },
  },
  emits: ['refrescar'],
  setup(props) {
    const paginaActual = ref(1)
    const porPagina    = ref(10)
    const orden        = ref({ campo: 'numero', asc: true })

    const filtros = ref({
      numero:      '',
      cedula:      '',
      nombre:      '',
      conArchivos: '',
    })

    // ── Filtrado ──────────────────────────────────────────────────────────
    const datosFiltrados = computed(() =>
      props.data.filter(item => {
        const f = filtros.value
        if (f.numero && !String(item.numero ?? '').toLowerCase().includes(f.numero.toLowerCase())) return false
        if (f.cedula && !String(item.cedula ?? '').toLowerCase().includes(f.cedula.toLowerCase())) return false
        if (f.nombre && !String(item.nombre ?? '').toLowerCase().includes(f.nombre.toLowerCase())) return false
        if (f.conArchivos === 'con' && item.totalArchivos == 0) return false
        if (f.conArchivos === 'sin' && item.totalArchivos >  0) return false
        return true
      })
    )

    // ── Ordenamiento ──────────────────────────────────────────────────────
    const datosOrdenados = computed(() => {
      const campo = orden.value.campo
      return [...datosFiltrados.value].sort((a, b) => {
        const va = a[campo] ?? ''
        const vb = b[campo] ?? ''
        const cmp = typeof va === 'number' ? va - vb : String(va).localeCompare(String(vb))
        return orden.value.asc ? cmp : -cmp
      })
    })

    // ── Paginación ────────────────────────────────────────────────────────
    const totalPaginas = computed(() =>
      Math.max(1, Math.ceil(datosOrdenados.value.length / porPagina.value))
    )
    const datosPaginados = computed(() => {
      const ini = (paginaActual.value - 1) * porPagina.value
      return datosOrdenados.value.slice(ini, ini + porPagina.value)
    })

    // ── Resumen ───────────────────────────────────────────────────────────
    const totalArchivos        = computed(() => datosFiltrados.value.reduce((a, r) => a + Number(r.totalArchivos), 0))
    const planillasSinArchivos = computed(() => datosFiltrados.value.filter(r => r.totalArchivos == 0).length)

    watch([filtros, orden, porPagina], () => { paginaActual.value = 1 }, { deep: true })

    // ── Helpers ───────────────────────────────────────────────────────────
    const ordenarPor = (campo) => {
      orden.value = orden.value.campo === campo
        ? { campo, asc: !orden.value.asc }
        : { campo, asc: true }
    }

    const iconOrden = (campo) => {
      if (orden.value.campo !== campo) return '⇅'
      return orden.value.asc ? '↑' : '↓'
    }

    const formatFecha = (fecha) => {
      if (!fecha) return '-'
      const d = new Date(fecha)
      return isNaN(d) ? fecha : d.toLocaleDateString('es-EC')
    }

    const limpiarFiltros = () => {
      filtros.value = { numero: '', cedula: '', nombre: '', conArchivos: '' }
      paginaActual.value = 1
    }

    const exportarCSV = () => {
      const cab  = ['Número', 'Cédula', 'Nombre', 'Total Archivos']
      const filas = datosOrdenados.value.map(r => [
        r.numero, r.cedula, r.nombre, r.totalArchivos
      ])
      const csv  = [cab, ...filas].map(f => f.map(v => `"${v ?? ''}"`).join(';')).join('\n')
      const blob = new Blob(['\uFEFF' + csv], { type: 'text/csv;charset=utf-8;' })
      const url  = URL.createObjectURL(blob)
      const a    = document.createElement('a')
      a.href     = url
      a.download = `planillas_archivos_${Date.now()}.csv`
      a.click()
      URL.revokeObjectURL(url)
    }

    return {
      filtros, paginaActual, porPagina, orden,
      datosFiltrados, datosPaginados, totalPaginas,
      totalArchivos, planillasSinArchivos,
      ordenarPor, iconOrden, formatFecha,
      limpiarFiltros, exportarCSV,
    }
  }
}
</script>

<style scoped>
.th-sort {
  cursor: pointer;
  user-select: none;
}
.th-sort:hover {
  background-color: #3d5166;
}
</style>